import {
    FetchState,
    DebitCardsState,
    DebitCardInfo
} from "@/shared/types";
import * as BankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: DebitCardsState = {
    debitCardsFetchState: FetchState.Uninitialized,
    isChangingStatus: false,
    debitCards: []
};

export const useDebitCardsStore = defineStore("debitCards", {
    state: (): DebitCardsState => (_.cloneDeep(initialState)),
    getters: {
        debitCardsLoading: state => state.debitCardsFetchState === FetchState.Loading
    },
    actions: {
        resetState () {
            this.debitCards = [];
            this.debitCardsFetchState = FetchState.Uninitialized;
        },

        setFetchState (fetchState: FetchState) {
            this.debitCardsFetchState = fetchState;
        },

        setDebitCardInfos (debitCards: DebitCardInfo[]) {
            this.debitCards = debitCards;
            this.debitCardsFetchState = FetchState.Loaded;
        },

        setDebitCardStatusChanging () {
            this.isChangingStatus = true;
        },

        setDebitCardActive (debitCardInfo: DebitCardInfo) {
            const debitCard = this.debitCards.find(i =>
                i.debitCardId === debitCardInfo.debitCardId);

            debitCard.isActivated = true;
            this.isChangingStatus = false;
        },

        setDebitCardClosed (debitCardInfo: DebitCardInfo) {
            this.isChangingStatus = false;
            const iToRemove = this.debitCards.findIndex(d => d.debitCardId === debitCardInfo.debitCardId);
            if (iToRemove < 0) return;

            this.debitCards.splice(iToRemove, 1);
        },

        setDebitCardIsAwaitingReissue (debitCardInfo: DebitCardInfo) {
            const debitCard = this.debitCards.find(i =>
                i.debitCardId === debitCardInfo.debitCardId);

            debitCard.isAwaitingReissue = true;
            debitCard.isActivated = false;
            this.isChangingStatus = false;
        },
        getDebitCards (): Promise<any> {
            if (this.debitCardsFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);
            return BankService.getDebitCards()
                .then(response => {
                    this.setDebitCardInfos(response.data);
                }).catch(err => {
                    this.resetState();
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        activateDebitCard (debitCardInfo: DebitCardInfo): Promise<any> {
            this.setDebitCardStatusChanging();
            return BankService.activateDebitCard(debitCardInfo)
                .then(response => {
                    this.setDebitCardActive(debitCardInfo);
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        closeDebitCard (debitCardInfo: DebitCardInfo): Promise<any> {
            this.setDebitCardStatusChanging();
            return BankService.deactivateDebitCard(debitCardInfo)
                .then(response => {
                    this.setDebitCardClosed(debitCardInfo);
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        reissueDebitCard (debitCardInfo: DebitCardInfo): Promise<any> {
            this.setDebitCardStatusChanging();
            return BankService.reissueDebitCard(debitCardInfo)
                .then(response => {
                    this.setDebitCardIsAwaitingReissue(debitCardInfo);
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});
